/**
 * Styles, die zum Verhalten gehören und deshalb mit dem Modul ausgeliefert werden.
 *
 * Sie liegen bewusst **nicht** in der CSS der einbindenden Komponente: `updateContent` setzt `aria-busy`,
 * und ohne die passende Regel würde dabei sichtbar nichts passieren — der Ladezustand wäre eine Zusage, die
 * jeder Konsument neu einlösen müsste.
 *
 * Der Weg über `adoptedStyleSheets` statt über eine `style.css` hat einen handfesten Grund: die thalia-cli
 * stellt jedem Selektor einer Komponenten-CSS den Elementnamen als *Nachfahren* voran, auch bei `@import`.
 * Aus `[aria-busy="true"] > *` würde `reihe-frame [aria-busy="true"] > *` — eine Bedingung am Container
 * selbst ließe sich so nicht ausdrücken. Zur Laufzeit ist der Tag-Name dagegen bekannt.
 *
 * Kein Risiko für ungestylte Zwischenzustände: die Regeln greifen ausschließlich, wenn das Modul-JS läuft.
 */

const uebernommeneTags = new Set();

/**
 * @param {string} tagName Elementname der einbindenden Komponente
 */
export function regeln(tagName) {
    return `
${tagName} > * {
    transition: opacity 120ms linear;
}

${tagName}[aria-busy="true"] > * {
    opacity: 0.55;
    transition-delay: 150ms;
}

@media (prefers-reduced-motion: reduce) {
    ${tagName} > * {
        transition-duration: 0s;
    }

    ::view-transition-group(*),
    ::view-transition-old(*),
    ::view-transition-new(*) {
        animation: none;
    }
}
`;
}

/**
 * Hängt die Regeln einmal pro Elementname an das Dokument. Kann der Browser keine konstruierbaren
 * Stylesheets, bleibt der Ladezustand unsichtbar — der Inhaltswechsel funktioniert weiterhin.
 */
export function styleUebernehmen(tagName) {
    if (uebernommeneTags.has(tagName) || !('adoptedStyleSheets' in document)) {
        return;
    }
    uebernommeneTags.add(tagName);
    try {
        const stylesheet = new CSSStyleSheet();
        stylesheet.replaceSync(regeln(tagName));
        document.adoptedStyleSheets = [...document.adoptedStyleSheets, stylesheet];
    } catch (error) {
        console.error(error);
    }
}
